import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Music, Flame, Zap, Guitar } from 'lucide-react';

const MusicVideos = () => {
  const [activeVideo, setActiveVideo] = useState(0);

  const videos = [
    {
      id: 1,
      title: "Smash The Player",
      subtitle: "Turn It Up Loud, London!",
      description: "Roxxie Vayne's explosive neon-glam metal anthem — a high-energy 80s-inspired rock music video filled with arcade chaos, heavy riffs, and cinematic visuals.",
      videoUrl: "https://customer-assets.emergentagent.com/job_23aab0a6-b4d8-4ab8-9e19-4bd12735db59/artifacts/8hmxfxyf_ROXXIE%20VAYNE%20%E2%80%93%20SMASH%20THE%20PLAYER%20%E2%9A%A1%20Turn%20It%20Up%20Loud%2C%20London%21.mp4",
      featured: true
    },
    {
      id: 2,
      title: "Midnight Accuser",
      subtitle: "80s Glam Metal Anthem",
      description: "A neon-glam anthem of lust, rebellion and danger. Platinum Puma's first official single — a full-blown 1980s glam metal fever dream where neon lightning splits the sky, smoke pours like fire, and the riff becomes a weapon. She's calling you out in the midnight flame.",
      videoUrl: "https://customer-assets.emergentagent.com/job_23aab0a6-b4d8-4ab8-9e19-4bd12735db59/artifacts/9knomemk_Roxxie%20Vayne%20%E2%80%93%20Midnight%20Accuser%20%2880s%20Glam%20Metal%20Anthem%29.mp4",
      featured: true
    }
  ];

  const currentVideo = videos[activeVideo];

  return (
    <section 
      id="music"
      data-testid="music-section"
      className="py-24 md:py-32 bg-void-black relative overflow-hidden"
    >
      {/* Section Divider */}
      <div className="section-divider w-full absolute top-0" />
      
      {/* Background Glow */}
      <div className="absolute inset-0 hero-glow opacity-50" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-manrope text-sm tracking-[0.3em] uppercase text-neon-red mb-4 flex items-center justify-center gap-2">
            <Zap className="w-4 h-4" />
            Welcome to the Roxxieverse
            <Zap className="w-4 h-4" />
          </p>
          <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight uppercase text-[#EDEDED]">
            Music Videos
          </h2>
          <p className="font-manrope text-neutral-400 mt-6 max-w-2xl mx-auto">
            Hair metal revival, retro synth-metal vibes, and powerful female-fronted rock.
          </p>
        </motion.div>

        {/* Main Video Player */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="relative rounded-lg overflow-hidden border-glow">
            {currentVideo.videoUrl ? (
              <video
                data-testid="main-video-player"
                className="w-full aspect-video bg-black"
                controls
                poster="https://customer-assets.emergentagent.com/job_23aab0a6-b4d8-4ab8-9e19-4bd12735db59/artifacts/25srhfr5_hf_20260202_151914_84ae40de-23f1-4549-8edc-af3ba777d7b1.png"
              >
                <source src={currentVideo.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="w-full aspect-video bg-void-gray flex items-center justify-center">
                <div className="text-center">
                  <Music className="w-16 h-16 text-neon-red/50 mx-auto mb-4" />
                  <p className="font-syne font-bold text-2xl text-neutral-400">Coming Soon</p>
                </div>
              </div>
            )}
            
            {/* Video Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
              <div className="flex items-center gap-3 mb-2">
                {currentVideo.featured && (
                  <span className="flex items-center gap-1 bg-neon-red px-2 py-1 rounded text-xs font-bold uppercase">
                    <Flame className="w-3 h-3" />
                    Now Playing
                  </span>
                )}
                <span className="text-neutral-400 text-sm font-manrope">Roxxie Vayne</span>
              </div>
              <h3 className="font-syne font-bold text-2xl md:text-3xl text-white">
                {currentVideo.title}
              </h3>
              <p className="font-manrope text-neutral-300 text-sm mt-1">
                {currentVideo.subtitle}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Video Description */}
        <motion.div 
          className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {/* Description */}
          <div className="lg:col-span-2 glass p-8 rounded-lg">
            <h4 className="font-syne font-bold text-xl text-[#EDEDED] mb-4 flex items-center gap-2">
              <Guitar className="w-5 h-5 text-neon-red" />
              About This Track
            </h4>
            <p className="font-manrope text-neutral-300 leading-relaxed mb-6">
              {currentVideo.description}
            </p>
            <p className="font-manrope text-neutral-400 text-sm leading-relaxed">
              If you love hair metal revival, retro synth-metal vibes, and powerful female-fronted rock — 
              <span className="text-neon-red font-medium"> welcome to the Roxxieverse.</span>
            </p>
            
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-6">
              {['#RoxxieVayne', '#GlamMetal', '#HairMetalRevival', '#SynthMetal', '#80sRock'].map((tag) => (
                <span 
                  key={tag}
                  className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs font-manrope text-neutral-400"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Playlist */}
          <div className="glass p-6 rounded-lg">
            <h4 className="font-syne font-bold text-lg text-[#EDEDED] mb-4 flex items-center gap-2">
              <Play className="w-4 h-4 text-neon-red" />
              Playlist
            </h4>
            <div className="space-y-3">
              {videos.map((video, index) => (
                <button
                  key={video.id}
                  data-testid={`playlist-item-${index}`}
                  onClick={() => setActiveVideo(index)}
                  className={`w-full text-left p-4 rounded-lg transition-all duration-300 ${
                    activeVideo === index 
                      ? 'bg-neon-red/20 border border-neon-red/50' 
                      : 'bg-white/5 border border-white/10 hover:border-neon-red/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center ${
                      activeVideo === index ? 'bg-neon-red' : 'bg-white/10'
                    }`}>
                      {activeVideo === index ? (
                        <Play className="w-4 h-4 fill-current" />
                      ) : (
                        <span className="font-manrope text-sm">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-manrope font-medium text-sm truncate ${
                        activeVideo === index ? 'text-white' : 'text-neutral-300'
                      }`}>
                        {video.title}
                      </p>
                      <p className="font-manrope text-xs text-neutral-500 truncate">
                        {video.subtitle}
                      </p>
                    </div>
                    {video.featured && (
                      <Flame className="w-4 h-4 text-neon-red flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Studio Tagline */}
        <motion.div 
          className="text-center glass p-8 rounded-lg border-glow"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <p className="font-manrope text-neutral-400 text-lg leading-relaxed mb-4">
            Want stunning worlds? Roxxie steps closer to the display.
            <br />
            She points, then swipes her hand...
          </p>
          <p className="font-syne font-bold text-2xl md:text-3xl text-[#EDEDED]">
            <span className="text-neon-red">ShadowCell Studios</span> doesn't just make clips...
          </p>
          <p className="font-syne font-extrabold text-3xl md:text-4xl text-white mt-2">
            We build <span className="text-neon-red">universes</span>.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MusicVideos;
